import * as d3 from 'd3';
import { scaleLinear } from 'd3-scale'; // Import D3 scaleLog
import { curveMonotoneX } from 'd3-shape';
import { filter } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Chart, ZoomButtons } from 'react-stockcharts';
import { CrossHairCursor, CurrentCoordinate } from 'react-stockcharts/lib/coordinates';
import { discontinuousTimeScaleProvider } from 'react-stockcharts/lib/scale';
import { AreaSeries, LineSeries } from 'react-stockcharts/lib/series';
import { last } from 'react-stockcharts/lib/utils';
import { useParams } from 'react-router-dom';

import Logo from '../../../../src/images/logo.png';
import ChartAxis from './ChartAxis';
import CommonChart from './CommonChart';
import ChartCanvas from './chartCanvas';
import { chartColors, chartTimeRangeMapper, ForecastName, ForeCastType } from '../../../shared/constants/api';
import { IChartData, IChartDetails, IChartItem, IChartSettingsType } from '../interface/chartImages.interface';
import { calculateAverages, customNumberFormatter, getChartDataBasedOnSpan, getColorForDataType } from '../../../shared/util/utility';

export enum ChartAxisPosition {
	LEFT_AXIS = 'left',
	RIGHT_AXIS = 'right'
}

export enum ScaleType {
	PERCENTAGE_CHANGE = 'percentageChange',
	LOGARITHAMIC_AXIS = 'logarithamicYaxis',
	PRICE_AXIS = 'price',
	PERCENTAGE_WITH_LOGARITHMIC = 'percentageWithLogarithmic'
}

export const renderTicks = (index: number) => {
	switch (index) {
		case 0:
		case 1:
			return {
				xTicks: 10,
				yTicks: 12
			};
		case 2:
		case 3: {
			return {
				xTicks: 3,
				yTicks: 3
			};
		}
		case 4:
		case 5:
		case 6: {
			return {
				xTicks: 3,
				yTicks: 2
			};
		}
		default:
			return {
				xTicks: 12,
				yTicks: 15
			};
	}
};

export const getStartDateForExport = (span: string, endDate: string, chartData: any) => {
	if (span === '1d') {
		return moment(endDate).subtract(1, 'day').format();
	} else if (span === '1w') {
		return moment(endDate).subtract(1, 'week').format();
	} else if (span === '1m') {
		return moment(endDate).subtract(1, 'month').format();
	} else if (span === '3m') {
		return moment(endDate).subtract(3, 'months').format();
	} else if (span === '6m') {
		return moment(endDate).subtract(6, 'months').format();
	} else if (span === '1y') {
		return moment(endDate).subtract(1, 'year').format();
	} else if (span === '3y') {
		return moment(endDate).subtract(3, 'years').format();
	} else if (span === '5y') {
		return moment(endDate).subtract(5, 'years').format();
	} else if (span === '10y') {
		return moment(endDate).subtract(10, 'years').format();
	} else if (span === 'ytd') {
		if (isEmpty(chartData)) {
			return moment(endDate).startOf('year').format();
		} else {
			return moment(chartData[chartData.length - 1].date)
				.startOf('year')
				.format();
		}
	} else if (span === 'max') {
		return chartData[0].date;
	}
};

export const formatDate = (date: any, format?: string) => {
	if (!date) {
		return '';
	}
	return moment(date)
		.local()
		.format(format?.toUpperCase() || 'DD-MM-YYYY');
};

interface IProps {
	chartData: any;
	chartType: string;
	showZoomButton: boolean;
	loading: boolean;
	chartDetails: any;
	cardIndex: number;
	span?: string;
	chartName: string;
	advanceChartConfig: any;
	isIntraday?: boolean;
	materialIndex?: number;
	chartSettings: any;
	selectedFrequencies?: { [materialKey: string]: string }
}

export interface ISTOIndicatorType {
	datum: number;
	tage: number;
	k: number;
}

export interface IMACDIndicatorInputType {
	kurz: number;
	signal: number;
	lang: number;
}

export interface IIndicatorConfig {
	keyName: string;
	indicatorKeyName: string;
	windowSize: number | ISTOIndicatorType | IMACDIndicatorInputType;
	value: string;
}

export interface IIndicatorObject {
	macd: {
		[key: string]: number;
	};
	sto: {
		[key: string]: number;
	};
}

export const englishLocale = {
	dateTime: '%x, %X',
	date: '%-m/%-d/%Y',
	time: '%-I:%M:%S %p',
	periods: ['AM', 'PM'],
	days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
	shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
	months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
	shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
};

export const germanLocale = {
	dateTime: '%A, der %e. %B %Y, %X',
	date: '%d.%m.%Y',
	time: '%H:%M:%S',
	periods: ['AM', 'PM'],
	days: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
	shortDays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
	months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
	shortMonths: ['Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
};

export const defineChartMargins = (advanceChartConfig: any, isOnDashboard: boolean, maxYValue = 0) => {
	const totalLeftAxis = filter(advanceChartConfig.chartSettings, {
		axis: { value: ChartAxisPosition.LEFT_AXIS }
	}).length;
	const totalRightAxis = filter(advanceChartConfig.chartSettings, {
		axis: { value: ChartAxisPosition.RIGHT_AXIS }
	}).length;

	const labelLength = maxYValue.toString().length;

	// Adjust margin for large label digits (6 digits or more)
	const dynamicMargin = labelLength > 5 ? labelLength * 10 : 60;

	if (totalLeftAxis > 0 && totalRightAxis > 0) {
		return { left: dynamicMargin, right: dynamicMargin };
	} else if (totalLeftAxis > 0 && totalRightAxis === 0) {
		return { left: isOnDashboard ? dynamicMargin + 5 : dynamicMargin, right: isOnDashboard ? 9 : 0 };
	} else if (totalRightAxis > 0 && totalLeftAxis === 0) {
		return { left: isOnDashboard ? 9 : 0, right: isOnDashboard ? dynamicMargin + 5 : dynamicMargin };
	}
	return { left: 60, right: 60 }; // fallback
};

const StockChart: React.FC<IProps> = (props) => {
	const params = useParams();
	const {
		chartData,
		chartType,
		span,
		chartDetails,
		cardIndex,
		showZoomButton,
		advanceChartConfig,
		loading,
		chartName,
		materialIndex,
		chartSettings,
		selectedFrequencies
	} = props;
	const [suffix, setSuffix] = useState(0);
	const maxYValue = useMemo(() => {
		let max = -Infinity;
		advanceChartConfig.chartSettings.forEach((setting: any) => {
			const key = `cv${setting.symbol}-${setting.boerse}-${materialIndex || 0}`;
			chartData.forEach((d: any) => {
				if (d[key] != null && d[key] > max) max = d[key];
			});
		});
		return max;
	}, [advanceChartConfig.chartSettings, chartData, materialIndex]);
	useEffect(() => {
		if (suffix !== 1) {
			setSuffix(1);
		}
	}, [suffix]);

	const chartDesignConfig = {
		margin: defineChartMargins(advanceChartConfig, false, maxYValue),
		fontSize: 12
	};

	const chartDataBasedOnType = useMemo(() => {
		if (chartType !== 'bar') {
			// For line and area charts, apply grouping frequency logic
			if (!chartData.length) {
				return chartData;
			}

			// Check if we have selected frequencies that differ from the default
			const hasCustomFrequencies = selectedFrequencies && Object.values(selectedFrequencies).some((freq) => freq && freq !== '1d');

			if (hasCustomFrequencies) {
				// Apply grouping based on selected frequencies
				const averages = calculateAverages([...chartData], chartDetails?.notierung);

				// Map frequency to the appropriate average data
				const frequencyToAverageMap: { [key: string]: any[] } = {
					'1d': averages.dailyAverage,
					'1w': averages.weeklyAverage,
					'1m': averages.monthlyAverage,
					'1y': averages.yearlyAverage
				};

				// Helper to normalize to span codes
				const normalizeToSpan = (val?: string) => {
					switch (val) {
						case 'D':
						case '1d':
							return '1d';
						case 'W':
						case '1w':
							return '1w';
						case 'M':
						case '1m':
							return '1m';
						case 'Y':
						case '1y':
							return '1y';
						default:
							return '1d';
					}
				};

				// Build date -> averagedRow maps for each span
				const buildDateMapForSpan = (rows: any[], spanCode: '1d' | '1w' | '1m' | '1y') => {
					const map = new Map<string, any>();
					rows.forEach((row) => {
						const base = moment(row.date);
						let start = base.clone();
						let end = base.clone();
						switch (spanCode) {
							case '1w':
								start = base.clone().startOf('isoWeek');
								end = base.clone().endOf('isoWeek');
								break;
							case '1m':
								start = base.clone().startOf('month');
								end = base.clone().endOf('month');
								break;
							case '1y':
								start = base.clone().startOf('year');
								end = base.clone().endOf('year');
								break;
							default:
								start = base.clone();
								end = base.clone();
						}
						const cur = start.clone();
						while (cur.isSameOrBefore(end, 'day')) {
							map.set(cur.format('YYYY-MM-DD'), row);
							cur.add(1, 'day');
						}
					});
					return map;
				};

				const map1d = buildDateMapForSpan(frequencyToAverageMap['1d'] || [], '1d');
				const map1w = buildDateMapForSpan(frequencyToAverageMap['1w'] || [], '1w');
				const map1m = buildDateMapForSpan(frequencyToAverageMap['1m'] || [], '1m');
				const map1y = buildDateMapForSpan(frequencyToAverageMap['1y'] || [], '1y');

				// Apply per-material frequency selection while preserving all dates
				return chartData.map((originalPoint: any) => {
					const dateKey = moment(originalPoint.date).format('YYYY-MM-DD');
					const mergedPoint: any = { ...originalPoint };
					let cumulativeIndex = 0;
					chartSettings.forEach((cs: { symbol: any; boerse: any; fields: string | any[]; }, idx: any) => {
						const matKeyIndex = typeof materialIndex === 'number' ? materialIndex : idx;
						const matKey = `${cs.symbol}-${cs.boerse}-${matKeyIndex}`;
						const rawFreq = selectedFrequencies && selectedFrequencies[matKey];
						const freq = normalizeToSpan(rawFreq);
						const avgRow =
							freq === '1w'
								? map1w.get(dateKey)
								: freq === '1m'
								? map1m.get(dateKey)
								: freq === '1y'
								? map1y.get(dateKey)
								: map1d.get(dateKey);
						const selectedTypes = advanceChartConfig.selectedDataTypesPerMaterial?.[matKey];
						const fieldsCount =
							selectedTypes && selectedTypes.length > 0 ? selectedTypes.length : Array.isArray(cs.fields) ? cs.fields.length : 1;
						const startIndex = cumulativeIndex;
						if (avgRow) {
							for (let j = 0; j < fieldsCount; j++) {
								const globalIdx = startIndex + j;
								const cvKey = `cv${cs.symbol}-${cs.boerse}-${globalIdx}`;
								const priceKey = `price${cs.symbol}-${cs.boerse}-${globalIdx}`;
								const percentageKey = `percentage${cs.symbol}-${cs.boerse}-${globalIdx}`;
								if ((avgRow as any)[cvKey] && (mergedPoint as any)[cvKey]) {
									(mergedPoint as any)[cvKey] = {
										...((mergedPoint as any)[cvKey] || {}),
										l: (avgRow as any)[cvKey]?.l
									};
								}
								if ((avgRow as any)[priceKey] !== undefined) {
									(mergedPoint as any)[priceKey] = (avgRow as any)[priceKey];
								}
								if ((avgRow as any)[percentageKey] !== undefined) {
									(mergedPoint as any)[percentageKey] = (avgRow as any)[percentageKey];
								}
							}
						}
						cumulativeIndex += fieldsCount;
					});
					// keep original x position
					mergedPoint.date = originalPoint.date;
					return mergedPoint;
				});
			}

			return chartData;
		}

		// Early return if no data
		if (!chartData.length) {
			return [];
		}

		const spanValue = chartTimeRangeMapper(chartData)[span as string];
		const averages = calculateAverages([...chartData], chartDetails?.notierung);
		const data = getChartDataBasedOnSpan(spanValue, averages);

		// Determine if we are on the dashboard and get the preferred forecast source
		// const isDashboard = location.pathname.includes('/dashboard');
		// const preferredSource = chartDetails.sources && chartDetails.sources.length > 0 ? chartDetails.sources[0] : undefined;

		// Process data in a single pass with optimized filtering
		return data;
	}, [chartType, chartData, span, chartDetails?.notierung, selectedFrequencies, advanceChartConfig.selectedDataTypesPerMaterial, chartSettings, materialIndex]);

	// Optimize memoized chart data calculation
	const memoizedChartData = useMemo(() => {
		if (chartType !== 'bar') {
			return chartDataBasedOnType;
		}

		// Filter out zero values more efficiently
		return chartDataBasedOnType.filter((item: IChartData) => {
			return Object.keys(item).some((key) => {
				if (!key.startsWith('cv')) return false;

				const value = item[key];
				if (typeof value !== 'object' || value === null) return false;

				const chartItem = value as IChartItem;
				return chartItem.e !== 0 || chartItem.h !== 0 || chartItem.l !== 0 || chartItem.t !== 0;
			});
		});
	}, [chartDataBasedOnType, chartType]);

	const xScaleProvider = discontinuousTimeScaleProvider
		.inputDateAccessor((d: any) => {
			return new Date(d.date);
		})
		.setLocale((params.language === 'en' && englishLocale) || germanLocale);

	const { data, xScale, xAccessor, displayXAccessor } = xScaleProvider(memoizedChartData);

	const xExtents = useMemo(() => {
		const start = xAccessor(last(data));
		const end = xAccessor(data[0]);
		return [start, end];
	}, [data, xAccessor]);
	
	const { leftAxisData, rightAxisData } = useMemo(
		() => ({
			leftAxisData: advanceChartConfig.chartSettings.filter((ele: any) => ele.axis.value === ChartAxisPosition.LEFT_AXIS),
			rightAxisData: advanceChartConfig.chartSettings.filter((ele: any) => ele.axis.value === ChartAxisPosition.RIGHT_AXIS)
		}),
		[advanceChartConfig.chartSettings]
	);

	const yScale = scaleLinear();
	
	const seriesStartIndexMap = useMemo(() => {
		const map: Record<string, number> = {};
		let cumulativeIndex = 0;
		chartSettings.forEach((chart: any, idx: number) => {
			const materialKey = `${chart.symbol}-${chart.boerse}-${materialIndex || idx}`;
			map[`${chart.id}`] = cumulativeIndex;
			const selected = advanceChartConfig.selectedDataTypesPerMaterial?.[materialKey];
			const count =
				selected && selected.length > 0 ? selected.length : Array.isArray(chart.fields) && chart.fields.length > 0 ? chart.fields.length : 1;
			cumulativeIndex += count;
		});
		return map;
	}, [chartSettings, materialIndex, advanceChartConfig.selectedDataTypesPerMaterial]);

	const getPercentageChangeYExtentKeys = useMemo(() => {
		const buildKeysForAxis = (axisData: IChartSettingsType[]) => {
			const keys: string[] = [];
			axisData.forEach((item) => {
				const findIndex = chartSettings.findIndex((chart:any) => chart.id === item.id);
				const materialKey = `${item.symbol}-${item.boerse}-${materialIndex || findIndex}`;
				const fields: string[] = Array.isArray((item as any).fields) ? ((item as any).fields as string[]) : [];
				const selected = advanceChartConfig.selectedDataTypesPerMaterial?.[materialKey];
				const dataTypes = selected && selected.length > 0 ? selected : fields.length > 0 ? [fields[0]] : ['l'];
				const startIndex = seriesStartIndexMap[`${item.id}`] || 0;
				dataTypes.forEach((dt: any) => {
					const localIdx = Math.max(0, fields.indexOf(dt));
					const seriesIdx = startIndex + localIdx;
					keys.push(`cv${item.symbol}-${item.boerse}-${seriesIdx}`);
				});
			});
			return keys;
		};

		return {
			leftAxisData: buildKeysForAxis(leftAxisData),
			rightAxisData: buildKeysForAxis(rightAxisData)
		};
	}, [leftAxisData, rightAxisData, chartSettings, materialIndex, advanceChartConfig.selectedDataTypesPerMaterial, seriesStartIndexMap]);
const { symbol, boerse } = chartDetails;
	// Check for sources directly on chartDetails or nested structure
	const sources = chartDetails?.sources || (chartDetails[`${symbol}-${boerse}-0`]?.sources);
	const colorList =
		!isEmpty(chartDetails) && sources !== undefined
			? sources.reduce((acc: Record<string, string>, key: string, index: number) => {
					acc[key] = chartColors[index];
					return acc;
			  }, {} as Record<string, string>)
			: {};

	const yExtentRightAxisRange = useMemo(() => {
		if (!data || data.length === 0) return [0, 0];

		const allYValues = data
			.flatMap((d: IChartData) => {
				return getPercentageChangeYExtentKeys.rightAxisData.map((key: string) => {
					const value = d[key];
					if (value && typeof value === 'object') {
						if ('source_data' in value && value.type !== ForeCastType.ACTUAL_PRICE) {
							const forecastData = value.source_data;
							if (forecastData && typeof forecastData === 'object') {
								const forecastValues = Object.values(forecastData).map((item) => (item as IChartItem)?.l);
								return Math.max(...forecastValues.filter((v) => v !== undefined && v !== null && !isNaN(v)));
							}
						} else if ('l' in value) {
							return value.l;
						}
					}
					return undefined;
				});
			})
			.filter((v: number | undefined) => v !== undefined && v !== null && !isNaN(v as number));

		const min = Math.min(...allYValues);
		const max = Math.max(...allYValues);
		return [min, max];
	}, [data, getPercentageChangeYExtentKeys.rightAxisData]);

	const yExtentLeftAxisRange = useMemo(() => {
		if (!data || data.length === 0) return [0, 0];

		const allYValues = data
			.flatMap((d: IChartData) => {
				return getPercentageChangeYExtentKeys.leftAxisData.map((key: string) => {
					const value = d[key];
					if (value && typeof value === 'object') {
						if ('source_data' in value && value.type !== ForeCastType.ACTUAL_PRICE) {
							const forecastData = value.source_data;
							if (forecastData && typeof forecastData === 'object') {
								const forecastValues = Object.values(forecastData).map((item) => (item as IChartItem)?.l);
								return Math.max(...forecastValues.filter((v) => v !== undefined && v !== null && !isNaN(v)));
							}
						} else if ('l' in value) {
							return value.l;
						}
					}
					return undefined;
				});
			})
			.filter((v: number | undefined) => v !== undefined && v !== null && !isNaN(v as number));

		const min = Math.min(...allYValues);
		const max = Math.max(...allYValues);
		return [min, max];
	}, [data, getPercentageChangeYExtentKeys.leftAxisData]);


	// Axis-specific maps of chart.id -> start index within the axis-specific flattened bar yAccessor array
	const barSeriesStartIndexMapLeft = useMemo(() => {
		const map: Record<string, number> = {};
		let cumulativeIndex = 0;
		// Use the full chartSettings prop, not advanceChartConfig.chartSettings which may be filtered
		chartSettings.forEach((chart: any, idx: number) => {
			const key = `${chart.symbol}-${chart.boerse}-${idx}`;
			const selected = advanceChartConfig.selectedDataTypesPerMaterial?.[key];
			map[`${chart.id}`] = cumulativeIndex;
			const count =
				selected && selected.length > 0 ? selected.length : Array.isArray(chart.fields) && chart.fields.length > 0 ? chart.fields.length : 1;
			cumulativeIndex += count;
		});
		return map;
	}, [chartSettings, advanceChartConfig.selectedDataTypesPerMaterial]);

	const barSeriesStartIndexMapRight = useMemo(() => {
		const map: Record<string, number> = {};
		let cumulativeIndex = 0;
		// Use the full chartSettings prop, not advanceChartConfig.chartSettings which may be filtered
		chartSettings.forEach((chart: any, idx: number) => {
			const key = `${chart.symbol}-${chart.boerse}-${idx}`;
			const selected = advanceChartConfig.selectedDataTypesPerMaterial?.[key];
			map[`${chart.id}`] = cumulativeIndex;
			const count =
				selected && selected.length > 0 ? selected.length : Array.isArray(chart.fields) && chart.fields.length > 0 ? chart.fields.length : 1;
			cumulativeIndex += count;
		});
		return map;
	}, [chartSettings, advanceChartConfig.selectedDataTypesPerMaterial]);


	// // Separate extent calculations for percentage charts - right axis
	// const yExtentRightAxisRangePercentage = useMemo(() => {
	// 	if (!data || data.length === 0) return [-0.05, 0.05];

	// 	// Define span categories
	// 	const isShortSpan = span === '3m' || span === '6m';
	// 	const isLongSpan = span === '1y' || span === '3y' || span === '5y' || span === '10y' || span === 'max';
	// 	const isExtremeSpan = span === '5y' || span === '10y' || span === 'max';

	// 	const allYValues = data
	// 		.flatMap((d: any) => {
	// 			return getPercentageChangeYExtentKeys.rightAxisData
	// 				.map((key) => {
	// 					const value = d[key];
	// 					if (value && typeof value === 'object' && 'lp' in value) {
	// 						const lp = value.lp;
	// 						// For 3m and 6m spans, use a wider range to prevent chart breaking
	// 						if (isShortSpan) {
	// 							return lp >= -1 && lp <= 1 ? lp : undefined; // -100% to 100%
	// 						}
	// 						// For 5y and max spans, use extreme range -100% to 100%
	// 						else if (isExtremeSpan) {
	// 							return lp >= -100 && lp <= 100 ? lp : undefined;
	// 						}
	// 						// For 1y and 3y spans, use -50% to 50%
	// 						else if (isLongSpan) {
	// 							return lp >= -0.5 && lp <= 0.5 ? lp : undefined;
	// 						}
	// 						// Default case for other spans
	// 						else {
	// 							return lp >= -0.6 && lp <= 0.6 ? lp : undefined;
	// 						}
	// 					}
	// 					return undefined;
	// 				})
	// 				.filter((v: any) => v !== undefined && !isNaN(v));
	// 		})
	// 		.filter((v: number | undefined) => v !== undefined && v !== null && !isNaN(v as number));

	// 	if (allYValues.length === 0) return [-0.05, 0.05];

	// 	// Use reduce to avoid call stack overflow
	// 	const min = allYValues.reduce((acc: number, val: number) => Math.min(acc, val), allYValues[0]);
	// 	const max = allYValues.reduce((acc: number, val: number) => Math.max(acc, val), allYValues[0]);

	// 	// Add padding based on span type
	// 	const range = max - min;
	// 	let padding;

	// 	if (isShortSpan) {
	// 		// Use larger padding for short spans to prevent chart breaking
	// 		padding = Math.max(range * 0.2, 0.05);
	// 	} else if (isExtremeSpan) {
	// 		padding = Math.max(range * 0.2, 0.05);
	// 	} else if (isLongSpan) {
	// 		padding = Math.max(range * 0.1, 0.02);
	// 	} else {
	// 		padding = Math.max(range * 0.05, 0.01);
	// 	}

	// 	return [min - padding, max + padding];
	// }, [data, getPercentageChangeYExtentKeys.rightAxisData, span]);

	// // Separate extent calculations for percentage charts
	// const yExtentLeftAxisRangePercentage = useMemo(() => {
	// 	if (!data || data.length === 0) return [-0.05, 0.05];

	// 	// Define span categories
	// 	const isShortSpan = span === '3m' || span === '6m';
	// 	const isLongSpan = span === '1y' || span === '3y' || span === '5y' || span === '10y' || span === 'max';
	// 	const isExtremeSpan = span === '5y' || span === '10y' || span === 'max';

	// 	const allYValues = data
	// 		.flatMap((d: any) => {
	// 			return getPercentageChangeYExtentKeys.leftAxisData
	// 				.map((key) => {
	// 					const value = d[key];
	// 					if (value && typeof value === 'object' && 'lp' in value) {
	// 						const lp = value.lp;
	// 						// For 3m and 6m spans, use a wider range to prevent chart breaking
	// 						if (isShortSpan) {
	// 							return lp >= -1 && lp <= 1 ? lp : undefined; // -100% to 100%
	// 						}
	// 						// For 5y and max spans, use extreme range -100% to 100%
	// 						else if (isExtremeSpan) {
	// 							return lp >= -100 && lp <= 100 ? lp : undefined;
	// 						}
	// 						// For 1y and 3y spans, use -50% to 50%
	// 						else if (isLongSpan) {
	// 							return lp >= -0.5 && lp <= 0.5 ? lp : undefined;
	// 						}
	// 						// Default case for other spans
	// 						else {
	// 							return lp >= -0.6 && lp <= 0.6 ? lp : undefined;
	// 						}
	// 					}
	// 					return undefined;
	// 				})
	// 				.filter((v: any) => v !== undefined && !isNaN(v));
	// 		})
	// 		.filter((v: number | undefined) => v !== undefined && v !== null && !isNaN(v as number));

	// 	if (allYValues.length === 0) return [-0.05, 0.05];

	// 	// Use reduce to avoid call stack overflow
	// 	const min = allYValues.reduce((acc: number, val: number) => Math.min(acc, val), allYValues[0]);
	// 	const max = allYValues.reduce((acc: number, val: number) => Math.max(acc, val), allYValues[0]);

	// 	// Add padding based on span type
	// 	const range = max - min;
	// 	let padding;

	// 	if (isShortSpan) {
	// 		// Use larger padding for short spans to prevent chart breaking
	// 		padding = Math.max(range * 0.2, 0.05);
	// 	} else if (isExtremeSpan) {
	// 		padding = Math.max(range * 0.2, 0.05);
	// 	} else if (isLongSpan) {
	// 		padding = Math.max(range * 0.1, 0.02);
	// 	} else {
	// 		padding = Math.max(range * 0.05, 0.01);
	// 	}

	// 	return [min - padding, max + padding];
	// }, [data, getPercentageChangeYExtentKeys.leftAxisData, span]);

	return (
		(chartDetails && !loading && chartDetails.disallowed === 0 && (
			<Fragment key={chartDetails.symbol}>
				{!isEmpty(chartData) && data.length <= 1 && (
					<div className='d-flex justify-content-center align-items-center' style={{ height: 500 }}>
						Not enough data to plot.
					</div>
				)}

				<div className='chart-image'>
					{!isEmpty(chartData) && data.length > 1 && (
						<>
							<ChartCanvas
								width={1141}
								height={700}
								seriesName={`MSFT_${suffix}`}
								data={data}
								type={'svg'}
								xAccessor={xAccessor}
								xScale={xScale}
								displayXAccessor={displayXAccessor}
								xExtents={xExtents}
								clamp={true}
								margin={chartDesignConfig.margin}
								ratio={1}
								mouseMoveEvent={true}
								panEvent={true}
								pointsPerPxThreshold={100}
								padding={20}
								className={`react-stockchart-${chartName}`}
								zoomMultiplier={span === '1w' ? 2 : 1.1}
							>
								<defs>
							{advanceChartConfig.chartSettings.map((chart: any) => {
										return (
											<Fragment key={`${chart.id}-${cardIndex}`}>
									<linearGradient id={`bar${chart.id}-${cardIndex}`} x1='0%' y1='0%' x2='0%' y2='100%'>
										<stop offset='0%' stopColor={chart.color} />
										<stop offset='100%' stopColor={chart.color} stopOpacity='0' />
									</linearGradient>
									<linearGradient
										id={`MyGradient${chart.symbol}-${chart.boerse}${chart.id}-${cardIndex}`}
										x1='100%'
										y1='-50%'
										x2='100%'
										y2='100%'
										gradientUnits='userSpaceOnUse'
									>
										<stop offset='0.326661' stopColor={chart.color} />
										<stop offset='1' stopColor={chart.color} stopOpacity='0' />
									</linearGradient>
									<linearGradient id={`MyGradientForecast`} x1='100%' y1='-50%' x2='100%' y2='100%' gradientUnits='userSpaceOnUse'>
										<stop offset='0.326661' stopColor='#FF9100' />
										<stop offset='1' stopColor='#FF9100' stopOpacity='0' />
									</linearGradient>
									{!isEmpty(chartDetails) && sources && chartType !== 'bar' ? (
										sources.map((item: string | number, index: number) => {
											return (
												<linearGradient
													id={`MyGradientListForecast${index}`}
													x1='100%'
													y1='-50%'
													x2='100%'
													y2='100%'
													gradientUnits='userSpaceOnUse'
												>
													<stop offset='0.326661' stopColor={`${colorList[item]}`} />
													<stop
														offset={index > 0 ? '0.55' : '1'}
														stopColor={`${colorList[item]}`}
														stopOpacity={index === 1 ? '0.3' : index === 2 ? '0.2' : '0'}
													/>
												</linearGradient>
											);
										})
									) : (
										<linearGradient
											id={`MyGradientForecast`}
											x1='100%'
											y1='-50%'
											x2='100%'
											y2='100%'
											gradientUnits='userSpaceOnUse'
										>
											<stop offset='0.326661' stopColor='#FF9100' />
											<stop offset='1' stopColor='#FF9100' stopOpacity='0' />
										</linearGradient>
									)}
									{!isEmpty(chartDetails) && sources && chartType === 'bar' ? (
										sources.map((item: string | number, index: number) => {
											return (
												<linearGradient
													id={`MyGradientListBarForecast${index}`}
													x1='0%'
													y1='50%'
													x2='100%'
													y2='0%'
													gradientUnits='userSpaceOnUse'
												>
													<stop offset='0.32665' stopColor={`${colorList[item]}`} />
													<stop
														offset={index > 0 ? '0.55' : '1'}
														stopColor={`${colorList[item]}`}
														stopOpacity={index === 1 ? '0.3' : index === 2 ? '0.2' : '0'}
													/>
												</linearGradient>
											);
										})
									) : (
										<linearGradient
											id={`MyGradientForecast`}
											x1='100%'
											y1='-50%'
											x2='100%'
											y2='100%'
											gradientUnits='userSpaceOnUse'
										>
											<stop offset='0.326661' stopColor='#FF9100' />
											<stop offset='1' stopColor='#FF9100' stopOpacity='0' />
										</linearGradient>
									)}
									<linearGradient id='MyGradientListForecast0' x1='0%' y1='10%' x2='90%' y2='0%' gradientUnits='userSpaceOnUse'>
										<stop offset='0.8' stop-color='#FF9100' />
										<stop offset='1' stop-color='#FF9100' stop-opacity='0' />
									</linearGradient>
								</Fragment>
										);
									})}
								</defs>

								{(advanceChartConfig.scaleType === ScaleType.PRICE_AXIS ||
									advanceChartConfig.scaleType === ScaleType.LOGARITHAMIC_AXIS) &&
									!isEmpty(rightAxisData) && (
										<Chart
											id={`${chartDetails.symbol}-${chartDetails.boerse}-${cardIndex}-1`}
											yExtents={yExtentRightAxisRange}
											height={500}
											yScale={yScale}
										>
											<>
												{rightAxisData.length > 0 && (
													<ChartAxis
														tickFormat={(value) =>
															customNumberFormatter(value, params.decimalSeparator ? params.decimalSeparator : '')
														}
														axisAt={ChartAxisPosition.RIGHT_AXIS}
														chartDesignConfig={chartDesignConfig}
														yTicks={renderTicks(cardIndex).yTicks}
														xTicks={renderTicks(cardIndex).xTicks}
													/>
												)}
												{rightAxisData.length > 0 &&
													rightAxisData.map((ele: any) => {
														return (
															<CommonChart
																cardIndex={cardIndex}
																key={ele.id}
																advanceChartConfig={advanceChartConfig}
																chartDetails={chartDetails}
																chartType={chartType}
																ele={ele}
																materialIndex={materialIndex}
																barSeriesStartIndexMap={barSeriesStartIndexMapRight}
																strokeColor={
																	sources !== undefined &&
																	sources.map((item: any) => colorList[item])
																}
																colorList={colorList}
																data={data}
																selectedKeys={
																	advanceChartConfig.selectedDataTypesPerMaterial &&
																	advanceChartConfig.selectedDataTypesPerMaterial[
																		`${ele.symbol}-${ele.boerse}-${
																			materialIndex || chartSettings.findIndex((c:any) => c.id === ele.id)
																		}`
																	]
																}
															/>
														);
													})}
											</>
											{showZoomButton && chartType !== 'bar' && (
												<ZoomButtons
													onReset={() => {
														setSuffix(suffix + 1);
													}}
												/>
											)}
										</Chart>
									)}
								{(advanceChartConfig.scaleType === ScaleType.PRICE_AXIS ||
									advanceChartConfig.scaleType === ScaleType.LOGARITHAMIC_AXIS) &&
									!isEmpty(leftAxisData) && (
										<Chart
											id={`${chartDetails.symbol}-${chartDetails.boerse}-${cardIndex}-2`}
											yExtents={yExtentLeftAxisRange}
											height={530}
											yScale={yScale}
										>
											<>
												{leftAxisData.length > 0 && (
													<ChartAxis
														tickFormat={(value) =>
															customNumberFormatter(value, params.decimalSeparator ? params.decimalSeparator : '')
														}
														axisAt={ChartAxisPosition.LEFT_AXIS}
														chartDesignConfig={chartDesignConfig}
														yTicks={renderTicks(cardIndex).yTicks}
														xTicks={renderTicks(cardIndex).xTicks}
													/>
												)}
												{leftAxisData.length > 0 &&
													leftAxisData.map((ele: any) => {
														return (
															<CommonChart
																cardIndex={cardIndex}
																key={ele.id}
																advanceChartConfig={advanceChartConfig}
																barSeriesStartIndexMap={barSeriesStartIndexMapLeft}
																chartDetails={chartDetails}
																chartType={chartType}
																ele={ele}
																materialIndex={materialIndex}
																strokeColor={
																	sources !== undefined &&
																	sources.map((item: any) => colorList[item])
																}
																colorList={colorList}
																data={data}
																selectedKeys={
																	advanceChartConfig.selectedDataTypesPerMaterial &&
																	advanceChartConfig.selectedDataTypesPerMaterial[
																		`${ele.symbol}-${ele.boerse}-${
																			materialIndex || chartSettings.findIndex((c: any) => c.id === ele.id)
																		}`
																	]
																}
															/>
														);
													})}
											</>
										</Chart>
									)}
								{advanceChartConfig.scaleType === ScaleType.PERCENTAGE_CHANGE && !isEmpty(leftAxisData) && (
									<Chart
										id={`${chartDetails.symbol}-${chartDetails.boerse}-${cardIndex}-3`}
										yExtents={(d: any) => {
											const values = getPercentageChangeYExtentKeys.leftAxisData.map((key: any) => {
												return d[key]?.p / 100;
											});
											return [...values];
										}}
										height={900}
									>
										<>
											{leftAxisData.length > 0 && (
												<ChartAxis
													tickFormat={d3.format('.2%')}
													axisAt={ChartAxisPosition.LEFT_AXIS}
													chartDesignConfig={chartDesignConfig}
													yTicks={renderTicks(cardIndex).yTicks}
													xTicks={renderTicks(cardIndex).xTicks}
												/>
											)}
											{chartType !== 'bar' &&
												leftAxisData.map((ele: any) => {
													const findIndex = advanceChartConfig.chartSettings.findIndex((chart: any) => chart.id === ele.id);
													const materialKey = `${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`;
													const fields: string[] = Array.isArray(ele.fields) ? (ele.fields as string[]) : [];
													const selected = advanceChartConfig.selectedDataTypesPerMaterial?.[materialKey];
													const dataTypes = selected && selected.length > 0 ? selected : fields.length > 0 ? [fields[0]] : ['l'];
													const startIndex = seriesStartIndexMap[`${ele.id}`] || 0;
													const yAccessors = dataTypes.map((dt: string) => {
														const localIdx = Math.max(0, fields.indexOf(dt));
														const seriesIdx = startIndex + localIdx;
														return (d: any) => {
															const chartData = d[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`];
															// Only return actual price data
															if (chartData?.type === ForeCastType.ACTUAL_PRICE || advanceChartConfig.chartSettings.length > 1) {
																return chartData?.p / 100;
															}
														};
													});
													
													// Create forecast accessors for percentage chart
													const forecastYAccessors = sources ? Object.keys(colorList).map((sourceKey) => {
														const seriesIdx = startIndex;
														return (d: any) => {
															const chartData = d[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`];
															const sourceData = chartData?.source_data;
															// Return forecast percentage or connect from last actual price
															if (chartData?.type === ForeCastType.FORECASTED_PRICE && sourceData?.[sourceKey]) {
																return sourceData[sourceKey]?.p / 100;
															}
														};
													}) : [];
													
													const strokeArray = new Array(yAccessors.length).fill(ele.color);
													const fills = yAccessors.map(
													() => `url(#MyGradient${ele.symbol}-${ele.boerse}${ele.id}-${cardIndex})`
												);
												
													return (
														<Fragment key={ele.id}>
															<RenderChart
																type={chartType}
																yAccessors={yAccessors}
																fills={fills}
																stroke={strokeArray}
																selectedDataTypes={
																	advanceChartConfig.selectedDataTypesPerMaterial &&
																	advanceChartConfig.selectedDataTypesPerMaterial[
																		`${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`
																	]
																}
															/>
															{advanceChartConfig.chartSettings.length === 1 && sources && forecastYAccessors.length > 0 && (
																<RenderChart
																	type={chartType}
																	yAccessors={forecastYAccessors}
																	fills={Object.keys(colorList).map((key, idx) => `url(#MyGradientListForecast${idx})`)}
																	stroke={Object.keys(colorList).map((key) => colorList[key])}
																	chartDetails={chartDetails}
																	selectedDataTypes={
																		advanceChartConfig.selectedDataTypesPerMaterial &&
																		advanceChartConfig.selectedDataTypesPerMaterial[
																			`${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`
																		]
																	}
																/>
															)}
														</Fragment>
													);
												})}
										</>
									</Chart>
								)}
								{advanceChartConfig.scaleType === ScaleType.PERCENTAGE_CHANGE && !isEmpty(rightAxisData) && (
									<Chart
										id={`${chartDetails.symbol}-${chartDetails.boerse}-${cardIndex}-4`}
									    yExtents={(d: any) => {
											const values = getPercentageChangeYExtentKeys.rightAxisData.map((key: any) => {
												return d[key]?.p / 100;
											});
											return [...values];
										}}
										height={530}
									>
										<>
											{rightAxisData.length > 0 && (
												<ChartAxis
													tickFormat={d3.format('.2%')}
													axisAt={ChartAxisPosition.RIGHT_AXIS}
													chartDesignConfig={chartDesignConfig}
													yTicks={renderTicks(cardIndex).yTicks}
													xTicks={renderTicks(cardIndex).xTicks}
												/>
											)}
											{chartType !== 'bar' &&
												rightAxisData.map((ele: any) => {
													const findIndex = advanceChartConfig.chartSettings.findIndex((chart: any) => chart.id === ele.id);
													const materialKey = `${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`;
													const fields: string[] = Array.isArray(ele.fields) ? (ele.fields as string[]) : [];
													const selected = advanceChartConfig.selectedDataTypesPerMaterial?.[materialKey];
													const dataTypes = selected && selected.length > 0 ? selected : fields.length > 0 ? [fields[0]] : ['l'];
													const startIndex = seriesStartIndexMap[`${ele.id}`] || 0;
													const yAccessors = dataTypes.map((dt: string) => {
														const localIdx = Math.max(0, fields.indexOf(dt));
														const seriesIdx = startIndex + localIdx;
														return (d: any) => {
															const chartData = d[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`];
															// Only return actual price data
															if (chartData?.type === ForeCastType.ACTUAL_PRICE || advanceChartConfig.chartSettings.length > 1) {
																return chartData?.p / 100;
															}
														};
													});
													
													// Create forecast accessors for percentage chart
													const forecastYAccessors = sources ? Object.keys(colorList).map((sourceKey) => {
														const seriesIdx = startIndex;
														return (d: any) => {
															const chartData = d[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`];
															const sourceData = chartData?.source_data;
															// Return forecast percentage or connect from last actual price
															if (chartData?.type === ForeCastType.FORECASTED_PRICE && sourceData?.[sourceKey]) {
																return sourceData[sourceKey]?.p / 100;
															}
														};
													}) : [];
													
													const strokeArray = new Array(yAccessors.length).fill(ele.color);
													return (
														<Fragment key={ele.id}>
															<RenderChart
																type={chartType}
																yAccessors={yAccessors}
																fill={`url(#MyGradient${ele.symbol}-${ele.boerse}${ele.id}-${cardIndex})`}
																stroke={strokeArray}
																selectedDataTypes={
																	advanceChartConfig.selectedDataTypesPerMaterial &&
																	advanceChartConfig.selectedDataTypesPerMaterial[
																		`${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`
																	]
																}
															/>
															{advanceChartConfig.chartSettings.length === 1 && sources && forecastYAccessors.length > 0 && (
																<RenderChart
																	type={chartType}
																	yAccessors={forecastYAccessors}
																	fills={Object.keys(colorList).map((key, idx) => `url(#MyGradientListForecast${idx})`)}
																	stroke={Object.keys(colorList).map((key) => colorList[key])}
																	chartDetails={chartDetails}
																	selectedDataTypes={
																		advanceChartConfig.selectedDataTypesPerMaterial &&
																		advanceChartConfig.selectedDataTypesPerMaterial[
																			`${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`
																		]
																	}
																/>
															)}
														</Fragment>
													);
												})}
										</>
									</Chart>
								)}
								<CrossHairCursor fontSize={chartDesignConfig.fontSize} strokeDasharray='Solid' />
								<image
									href={Logo}
									x={chartName === 'history' ? 900 : 910}
									y={chartType === 'area' ? 470 : 500}
									width='142'
									height='31'
								/>
							</ChartCanvas>
								<div className={`chart-label chart-${chartName}`}>
									{advanceChartConfig.chartSettings.map((chart: any) => {
											const hasMatflixxForecast =
													params.isForeCastInclude === 'true' &&
													Object.keys(chart.forecastedColor || {}).some((key) => {
														const forecastKey = key.replace(/-\d+$/, '');
														return (
														forecastKey === ForecastName.MATFLIXX_FORECAST ||
														forecastKey === ForecastName.MATFLIXX_AI_FORECAST
														);
													});

									return (
										<div key={chart.id} className='chart-info'>
											<label className='chart'>
												<span className='chart-color' style={{ backgroundColor: `${chart.color}` }} />
												<span className='chart-name'>{chart.name}</span>
												<span>
													{chart.currency.value} / {chart.unit.value}
												</span>
											</label>
											{hasMatflixxForecast && !isEmpty(chartSettings) && chartSettings.length < 2 && (() => {
												// Build dynamic forecast legend items
												const items: Array<{ key: string; label: string; color: string }> = [];
												// Prefer sources/colorList if available
											   if(chart.forecastedColor && typeof chart.forecastedColor === 'object') {
													const added: Record<string, boolean> = {};
													Object.entries(chart.forecastedColor).forEach(([rawKey, color]) => {
														const baseKey = rawKey.replace(/-\d+$/, '');
														if (added[baseKey]) return;
														added[baseKey] = true;
														const label = baseKey;
														items.push({ key: `${chart.id}-${baseKey}`, label, color: String(color) });
													});
												}
												return (
													<>
														{items.map((data) => (
															<div className='chart' key={data.key}>
																<span className='chart-color' style={{ backgroundColor: data.color }} />
																<span className='chart-name'>{data.label}</span>
															</div>
														))}
													</>
												);
											})()}
										</div>
									);
								})}
							</div>
						</>
					)}
				</div>
			</Fragment>
		)) || <></>
	);
};

export const RenderChart: React.FC<{
	type: string;
	yAccessor?: (d: any) => any; // Single accessor for backward compatibility
	yAccessors?: Array<(d: any) => any>; // Multiple accessors for new implementation
	fill?: string; // Single fill for backward compatibility
	fills?: string[]; // Multiple fills for new implementation
	stroke: string | string[]; // Support for multiple or single strokes
	canvasGradient?: any;
	filter?: any;
	chartDetails?: IChartDetails;
	selectedDataTypes?: string[];
}> = ({ type, yAccessor, yAccessors, fill, fills = [], stroke, canvasGradient, chartDetails,selectedDataTypes }) => {
	const useMultipleAccessors = Array.isArray(yAccessors) && yAccessors.length > 0;
	const isForecastValue = chartDetails && chartDetails?.forecastAccess === 0;
	const svgRef = useRef<SVGSVGElement>(null);


	// Handle backward compatibility for single accessor/fill
	const accessorArray = useMultipleAccessors ? yAccessors : (yAccessor ? [yAccessor] : []);
	const fillArray = useMultipleAccessors ? fills : [fill || 'none'];
	const strokeArray = Array.isArray(stroke) ? stroke : [stroke];
	
	// Filter out any undefined or null accessors
	const validAccessors = accessorArray.filter(accessor => typeof accessor === 'function');
	
	return (
		<>
				{type === 'area' &&
				validAccessors.map((accessor, index) => {
					const shouldShowMultipleDataTypes = selectedDataTypes && selectedDataTypes.length > 0;
					const baseColor = strokeArray[index] || '#4472c4';
					const chartGroupingColor = getColorForDataType(baseColor, index, selectedDataTypes || []);

					return (
						<Fragment key={index}>
							<AreaSeries
								yAccessor={accessor}
								fill={shouldShowMultipleDataTypes ? chartGroupingColor : fillArray[index] || 'none'}
								stroke={shouldShowMultipleDataTypes ? chartGroupingColor : strokeArray[index] || 'none'}
								strokeWidth={1}
								interpolation={curveMonotoneX}
								canvasGradient={canvasGradient}
								clip={false}
								className={`${isForecastValue && 'blur'}`}
								svgRef={svgRef}
							/>
							<CurrentCoordinate yAccessor={accessor} fill={fillArray[index] || strokeArray[index]} />
						</Fragment>
					);
				})}
				{type === 'line' &&
				validAccessors.map((accessor, index) => {
					const shouldShowMultipleDataTypes = selectedDataTypes && selectedDataTypes.length > 0;
					const baseColor = strokeArray[index] || '#4472c4';
					const chartGroupingColor = getColorForDataType(baseColor, index, selectedDataTypes || []);

					return (
						<Fragment key={index}>
							<LineSeries
								yAccessor={accessor}
								stroke={shouldShowMultipleDataTypes ? chartGroupingColor : strokeArray[index] || 'none'}
								strokeWidth={2}
								clip={false}
								interpolation={curveMonotoneX}
								className={`${isForecastValue && 'blur'}`}
								svgRef={svgRef}
							/>
							<CurrentCoordinate yAccessor={accessor} fill={strokeArray[index]} />
						</Fragment>
					);
				})}
		</>
	);
};

export default StockChart;
